import { useContext, useEffect, useMemo } from 'react'
import ChatSection from '../../components/Layout/ChatSection'
import Header from '../../components/Layout/Header'
import StreamInfoPanel from '../../components/Layout/StreamInfoPanel'
import VideoSection from '../../components/Layout/VideoSection'
import AdBanners from '../../components/Utilities/AdBanners/AdBanners'
import * as FEATURES from '../../constants/features'
import * as SETTINGS from '../../constants/settings'
import { AuthenticationContext } from '../../context/authenticationContext'
import { SiteConfigContext } from '../../context/siteConfigContext'
import { useViewportHeight } from '../../hooks/useViewportHeight'
import { hasStaffPrivilege } from '../../utils/RequireRole'
import {
  runOneSignal,
  setPageTitleDefault,
  userCanAccessLimitedAccess
} from '../../utils/utilities'

const Chatroom = () => {
  useViewportHeight()
  setPageTitleDefault()
  const { user } = useContext(AuthenticationContext)
  const {
    [SETTINGS.FEATURES.key]: enabledFeatures,
    [SETTINGS.ONESIGNAL_APP_ID.key]: oneSignalAppId,
    [SETTINGS.DISPLAY_BANNER.key]: displayBanner,
    [SETTINGS.LIMITED_ACCESS.key]: limitedAccess
  } = useContext(SiteConfigContext)

  const limitedAccessEnabled = useMemo(
    () => enabledFeatures && enabledFeatures[FEATURES.LIMITED_ACCESS],
    [enabledFeatures]
  )

  useEffect(() => {
    if (user && oneSignalAppId) {
      handleOneSignal()
    }
  }, [user, oneSignalAppId])

  const handleOneSignal = async () => {
    const allowLocal = process.env.NODE_ENV === 'development'
    runOneSignal(oneSignalAppId, user.userService.email, allowLocal)
  }

  if (
    limitedAccessEnabled &&
    !hasStaffPrivilege(user) &&
    !userCanAccessLimitedAccess(user, limitedAccess)
  ) {
    return (
      <main className="set-vh flex h-screen max-h-screen flex-col overflow-hidden bg-white dark:bg-black">
        <Header />
        <div className="min-h-0 flex-1 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center py-4">
            <LimitedAccessBanner />
          </div>
        </div>
      </main>
    )
  }

  return (
    <main className="set-vh flex h-screen max-h-screen flex-col overflow-hidden bg-white dark:bg-black">
      <Header />
      <div className="flex flex-col overflow-auto md:flex-row md:overflow-y-hidden h-full">
        <div className="flex flex-col md:overflow-y-auto md:overflow-x-visible scrollbar-hide md:ml-2 md:pt-2 md:gap-2 w-full">
          {displayBanner && (
            <div className="flex">
              <div className="mx-auto w-full p-0">
                <AdBanners />
              </div>
            </div>
          )}
          <div className="bg-black md:rounded-xl overflow-clip-nosafari">
            <VideoSection />
          </div>
          <StreamInfoPanel />
        </div>
        <ChatSection />
      </div>
    </main>
  )
}

export const LimitedAccessBanner = () => {
  const { [SETTINGS.LIMITED_ACCESS.key]: limitedAccess } =
    useContext(SiteConfigContext)

  return (
    <section
      aria-label="Limited access"
      className="m-4 w-full max-w-2xl overflow-hidden rounded-xl border border-brand-primary-700/60 bg-neutral-50 dark:bg-neutral-950 shadow-lg shadow-black/30 md:mx-6"
    >
      <div className="h-1 bg-gradient-to-r from-brand-primary-500 via-brand-primary-400 to-brand-primary-600" />
      <div className="p-6 sm:p-8">
        <div
          className="h-full p-4 mx-auto prose dark:prose-invert"
          dangerouslySetInnerHTML={{ __html: limitedAccess.copy }}
        />
      </div>
    </section>
  )
}

export default Chatroom
